import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {css, Theme, useTheme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {elevation} from "@pg-design/elevation";
import {Col, Container, Row} from "@pg-design/grid";
import {borderRadius, calculateRemSize, mb, mt, onDesktop, p, ph, pv} from "@pg-design/helpers-css";
import {Picture} from "@pg-design/picture";
import {Text} from "@pg-design/text";

import {breakpoints} from "../../../common/app/styles/breakpoints";
import {LANDING_BIG_DATA_CONTACT_SECTION_ID} from "./hero/Hero";

import background from "../../monitoring/assets/map_bg.png";
import background_large from "../../monitoring/assets/map_bg_large.png";
import background_medium from "../../monitoring/assets/map_bg_medium.png";
import background_mobile from "../../monitoring/assets/map_bg_mobile.png";
import map_desktop from "../../monitoring/assets/map_desktop.svg";
import map_mobile from "../../monitoring/assets/map_mobile.svg";

type DecisionCardData = {
    title: string;
    description: string;
    comingSoonLabel?: string;
};

const scrollToContactForm = () => {
    document.getElementById(LANDING_BIG_DATA_CONTACT_SECTION_ID)?.scrollIntoView({behavior: "smooth"});
};

interface IDecisionCardProps {
    title: string;
    description: string;
    comingSoonLabel?: string;
}

const DecisionCard = ({title, description, comingSoonLabel}: IDecisionCardProps) => {
    const theme = useTheme();

    return (
        <div css={decisionCard}>
            <div css={titleRow}>
                <Text strong variant="body_copy_1" align="center" as="h3" css={decisionTitle}>
                    {title}
                </Text>
                {comingSoonLabel ? <span css={soonBadge(theme)}>{comingSoonLabel}</span> : null}
            </div>
            <Text variant="body_copy_1" align="center" color={theme.colors.gray[800]}>
                {description}
            </Text>
        </div>
    );
};

export const AnalyticalPlatformReasons = () => {
    const {t} = useTranslation();
    const theme = useTheme();

    const cards = useMemo<DecisionCardData[]>(() => {
        const comingSoonLabel = t("landing.analyticalPlatformReasons.comingSoon");

        return [
            {
                title: t("landing.analyticalPlatformReasons.card1.title"),
                description: t("landing.analyticalPlatformReasons.card1.description")
            },
            {
                title: t("landing.analyticalPlatformReasons.card2.title"),
                description: t("landing.analyticalPlatformReasons.card2.description")
            },
            {
                title: t("landing.analyticalPlatformReasons.card3.title"),
                description: t("landing.analyticalPlatformReasons.card3.description")
            },
            {
                title: t("landing.analyticalPlatformReasons.card4.title"),
                description: t("landing.analyticalPlatformReasons.card4.description")
            },
            {
                title: t("landing.analyticalPlatformReasons.card5.title"),
                description: t("landing.analyticalPlatformReasons.card5.description")
            },
            {
                title: t("landing.analyticalPlatformReasons.card6.title"),
                description: t("landing.analyticalPlatformReasons.card6.description"),
                comingSoonLabel
            },
            {
                title: t("landing.analyticalPlatformReasons.card7.title"),
                description: t("landing.analyticalPlatformReasons.card7.description"),
                comingSoonLabel
            },
            {
                title: t("landing.analyticalPlatformReasons.card8.title"),
                description: t("landing.analyticalPlatformReasons.card8.description"),
                comingSoonLabel: t("landing.analyticalPlatformReasons.comingSoonNewFeatures")
            }
        ];
    }, [t]);

    const cardRows = useMemo((): DecisionCardData[][] => {
        const rows: DecisionCardData[][] = [];
        for (let i = 0; i < cards.length; i += 2) {
            rows.push(cards.slice(i, i + 2));
        }
        return rows;
    }, [cards]);

    return (
        <Container as="section" fluid css={wrapper}>
            <Container>
                <Text css={mb(6)} variant="headline_1" align="center" as="h2">
                    {t("landing.analyticalPlatformReasons.heading")}
                </Text>
            </Container>

            <div css={content(theme)}>
                <Container css={stage}>
                    <Row>
                        <Col lgOffset={2} lg={12} md={10} sm={4} css={cardsCol}>
                            <div css={cardsCluster}>
                                {cardRows.map((pair, rowIdx) => (
                                    <div key={rowIdx} css={cardRowGrid}>
                                        {pair.map((card, colIdx) => (
                                            <div key={`${rowIdx}-${colIdx}`} css={cardGridCell}>
                                                <DecisionCard
                                                    title={card.title}
                                                    description={card.description}
                                                    comingSoonLabel={card.comingSoonLabel}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    <div css={ctaOnCards}>
                        <Button variant="filled_primary" size="big" css={ctaButton} onClick={scrollToContactForm}>
                            {t("landing.analyticalPlatformReasons.cta")}
                        </Button>
                    </div>

                    <div css={mapFloat}>
                        <Picture
                            sources={[
                                {minWidthPX: 0, src: map_mobile, height: 360, width: 355},
                                {
                                    minWidthPX: theme.breakpoints.numeric.lg,
                                    src: map_desktop,
                                    height: 409,
                                    width: 402
                                }
                            ]}
                            alt={t("landing.analyticalPlatformReasons.mapAlt")}
                            loading="lazy"
                        />
                    </div>
                </Container>
            </div>
        </Container>
    );
};

const decisionCard = css`
    ${p(3, 2)};
    background-color: #fff;
    ${elevation(4)};
    ${borderRadius(2)};
    width: 100%;
    box-sizing: border-box;

    ${onDesktop(css`
        width: 360px;
        max-width: 100%;
        ${p(4, 3.5)};
    `)}
`;

const titleRow = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${calculateRemSize(1)};
    ${mb(2)};
`;

const decisionTitle = css`
    margin: 0;
`;

const soonBadge = (theme: Theme) => css`
    display: inline-flex;
    padding: 2px 10px;
    border-radius: 999px;
    background: ${theme.colors.gray[200]};
    color: ${theme.colors.gray[700]};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    text-transform: lowercase;
`;

const wrapper = (theme: Theme) => css`
    text-align: center;
    overflow: hidden;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(9)};
    }
`;

const content = (theme: Theme) => css`
    position: relative;
    background: url(${background_mobile}) no-repeat;
    background-size: cover;
    background-position: center top;
    padding-bottom: ${calculateRemSize(8)};

    @media (min-width: ${theme.breakpoints.xs}) {
        background: url(${background_medium}) no-repeat;
        background-size: cover;
        background-position: center top;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        background: url(${background}) no-repeat;
        background-size: cover;
        background-position: center top;
    }

    @media (min-width: ${theme.breakpoints.xl}) {
        background: url(${background_large}) no-repeat;
        background-size: cover;
        background-position: center top;
    }
`;

const stage = css`
    position: relative;
`;

const mapFloat = css`
    display: flex;
    justify-content: center;
    margin-top: ${calculateRemSize(2)};
    pointer-events: none;

    ${onDesktop(css`
        position: absolute;
        left: 2%;
        top: 18%;
        margin-top: 0;
        margin-bottom: 0;
        justify-content: flex-start;
    `)}
`;

const cardsCol = css`
    position: relative;
    z-index: 1;
`;

const cardsCluster = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${calculateRemSize(2)};
    ${pv(2)};
`;

const cardGridCell = css`
    min-width: 0;
    display: flex;
    justify-content: center;
`;

const cardRowGrid = css`
    display: grid;
    width: 100%;
    max-width: calc(360px * 2 + ${calculateRemSize(2)});
    grid-template-columns: minmax(0, 360px);
    justify-content: center;
    gap: ${calculateRemSize(2)};

    ${onDesktop(css`
        grid-template-columns: 360px 360px;
    `)}
`;

const ctaOnCards = css`
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 0 auto;
    ${mt(2)};
`;

const ctaButton = css`
    ${ph(6)};
    text-transform: uppercase;
    font-weight: 700;
    border-radius: 9999px;
    width: 100%;

    @media (min-width: ${breakpoints.xxs}) {
        width: auto;
        min-width: 20.8rem;
    }
`;
